import { gql } from "@apollo/client";
import { Checkbox } from "@mui/material";
import Stack from "atoms/Stack";
import { MOBILE_LAYOUT_BREAKPOINT } from "helpers/breakpoints";
import { COLOR_BLACK, COLOR_GREY_BORDER, COLOR_WHITE } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING, SPACING_DENSE } from "helpers/spacings";
import useData from "helpers/useData";
import Section, { SectionLabel } from "molecules/Section";
import HighlightPlayerDialog from "pages/video_player/HighlightPlayerDialog";
import React, { useMemo, useRef, useState } from "react";
import { useWindowSize } from "react-use-size";

// FIBA half-court: portrait, hoop at top. 15m wide × 14m long.
const S = 30; // 30 px per meter
const BACKBOARD_WIDTH = 1.8 * S;
const BACKBOARD_FROM_BASELINE = 1.2 * S;
const BASKETBALL_RIM_R = (0.45 / 2) * S;
const SVG_W = 15 * S; // 450px wide
const SVG_H = 14 * S; // 420px height
const BX = SVG_W / 2;
const BY = 1.575 * S;
const KEY_LEN = 5.8 * S;
const KEY_HALF = 2.45 * S;
const FT_R = 1.8 * S;
const PT3_R = 6.75 * S;
const PT3_SIDE = 0.9 * S;
const RA_R = 1.25 * S;
const PT3_X_L = PT3_SIDE;
const PT3_X_R = SVG_W - PT3_SIDE;
const PT3_Y = BY + Math.sqrt(PT3_R ** 2 - (BX - PT3_X_L) ** 2);
const NZ_MARK_W = 0.1 * S;
const NZ_TICK_YS = [1.75, 3.85, 4.7].map((m) => m * S);
const NZ_BLOCK_Y = 2.6 * S;
const NZ_BLOCK_H = 0.4 * S;

// convert GL coordinations (0–100) → SVG px
const toSvgX = (dx) => (1 - dx / 100) * SVG_W;
const toSvgY = (dy) => (1 - dy / 100) * SVG_H;

const CHART_BG = "#F2F2F0";
const PAINT_BG = "#F2F2F0";
const LINE = "#3C414A";
const LW = 1.5;

const MAKE_COLOR = "#1F5F63";
const MISS_COLOR = "#D92D20";
const SHOT_OPACITY = 0.75;
const MAKE_STROKE = 2.3;
const MISS_STROKE = 1.8;
const MARKER_R = 5.5;

function downloadSvgAsPng(svgEl, filename) {
  const scale = 2;
  const w = SVG_W * scale;
  const h = SVG_H * scale;
  const clone = svgEl.cloneNode(true);
  clone.setAttribute("width", w);
  clone.setAttribute("height", h);
  const svgData = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  const img = new Image();
  img.onload = () => {
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    const link = document.createElement("a");
    link.download = filename;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  img.src = url;
}

function ShotChartSvg({ events, svgRef, onClickEvent }) {
  return (
    <svg ref={svgRef} viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ width: "100%", height: "auto", display: "block" }}>
      {/* Background */}
      <rect width={SVG_W} height={SVG_H} fill={CHART_BG} />
      {/* Paint area */}
      <rect x={BX - KEY_HALF} y={0} width={KEY_HALF * 2} height={KEY_LEN} fill={PAINT_BG} />
      {/* Court boundary */}
      <rect x={0} y={0} width={SVG_W} height={SVG_H} fill="none" stroke={LINE} strokeWidth={LW} />
      {/* Key outline */}
      <rect x={BX - KEY_HALF} y={0} width={KEY_HALF * 2} height={KEY_LEN} fill="none" stroke={LINE} strokeWidth={LW} />
      {/* Neutral zone marks */}
      {NZ_TICK_YS.map((y, i) => (
        <React.Fragment key={i}>
          <line x1={BX - KEY_HALF - NZ_MARK_W} y1={y} x2={BX - KEY_HALF} y2={y} stroke={LINE} strokeWidth={LW} />
          <line x1={BX + KEY_HALF} y1={y} x2={BX + KEY_HALF + NZ_MARK_W} y2={y} stroke={LINE} strokeWidth={LW} />
        </React.Fragment>
      ))}
      <rect x={BX - KEY_HALF - NZ_MARK_W} y={NZ_BLOCK_Y} width={NZ_MARK_W} height={NZ_BLOCK_H} fill={LINE} />
      <rect x={BX + KEY_HALF} y={NZ_BLOCK_Y} width={NZ_MARK_W} height={NZ_BLOCK_H} fill={LINE} />
      {/* Free throw semicircle */}
      <path
        d={`M ${BX - FT_R},${KEY_LEN} A ${FT_R},${FT_R} 0 1,0 ${BX + FT_R},${KEY_LEN}`}
        fill="none"
        stroke={LINE}
        strokeWidth={LW}
      />
      {/* 3pt line */}
      <path
        d={`M ${PT3_X_L},0 L ${PT3_X_L},${PT3_Y} A ${PT3_R},${PT3_R} 0 0,0 ${PT3_X_R},${PT3_Y} L ${PT3_X_R},0`}
        fill="none"
        stroke={LINE}
        strokeWidth={LW}
      />
      {/* Restricted area */}
      <path
        d={`M ${BX - RA_R},${BACKBOARD_FROM_BASELINE} L ${BX - RA_R},${BY} A ${RA_R},${RA_R} 0 0,0 ${BX + RA_R},${BY} L ${BX + RA_R},${BACKBOARD_FROM_BASELINE}`}
        fill="none"
        stroke={LINE}
        strokeWidth={LW}
      />
      {/* Line from rim to backboard */}
      <line x1={BX} y1={BY - BASKETBALL_RIM_R} x2={BX} y2={BACKBOARD_FROM_BASELINE} stroke={LINE} strokeWidth={LW} />
      {/* Backboard */}
      <line
        x1={BX - BACKBOARD_WIDTH / 2}
        y1={BACKBOARD_FROM_BASELINE}
        x2={BX + BACKBOARD_WIDTH / 2}
        y2={BACKBOARD_FROM_BASELINE}
        stroke={LINE}
        strokeWidth={2}
      />
      {/* Basket */}
      <circle cx={BX} cy={BY} r={BASKETBALL_RIM_R} fill="none" stroke={LINE} strokeWidth={2} />

      {/* Shot markers */}
      {events.map((event) => {
        const cx = toSvgX(event.coordinates.y);
        const cy = toSvgY(event.coordinates.x);
        const HIT_R = MARKER_R * 2;
        if (event.made) {
          return (
            <g
              key={event.id}
              opacity={SHOT_OPACITY}
              style={{ cursor: "pointer" }}
              onClick={() => onClickEvent(event.id)}
            >
              <circle cx={cx} cy={cy} r={MARKER_R} fill="none" stroke={MAKE_COLOR} strokeWidth={MAKE_STROKE} />
              <circle cx={cx} cy={cy} r={HIT_R} fill="transparent" stroke="none" />
            </g>
          );
        }
        const d = MARKER_R;
        return (
          <g key={event.id} opacity={SHOT_OPACITY} style={{ cursor: "pointer" }} onClick={() => onClickEvent(event.id)}>
            <line
              x1={cx - d}
              y1={cy - d}
              x2={cx + d}
              y2={cy + d}
              stroke={MISS_COLOR}
              strokeWidth={MISS_STROKE}
              strokeLinecap="round"
            />
            <line
              x1={cx - d}
              y1={cy + d}
              x2={cx + d}
              y2={cy - d}
              stroke={MISS_COLOR}
              strokeWidth={MISS_STROKE}
              strokeLinecap="round"
            />
            <circle cx={cx} cy={cy} r={HIT_R} fill="transparent" stroke="none" />
          </g>
        );
      })}

      {/* Legend bottom-right — fontSize 13 */}
      <g transform={`translate(${SVG_W - 108}, ${SVG_H - 16})`}>
        <circle cx={0} cy={6} r={5} fill="none" stroke={MAKE_COLOR} strokeWidth={MAKE_STROKE} />
        <text x={10} y={11} fontSize={13} fill={LINE} fontFamily={FONT_FAMILY_GRAPHIK}>
          Make
        </text>
        <line x1={49} y1={1} x2={59} y2={11} stroke={MISS_COLOR} strokeWidth={MISS_STROKE} strokeLinecap="round" />
        <line x1={49} y1={11} x2={59} y2={1} stroke={MISS_COLOR} strokeWidth={MISS_STROKE} strokeLinecap="round" />
        <text x={65} y={11} fontSize={13} fill={LINE} fontFamily={FONT_FAMILY_GRAPHIK}>
          Miss
        </text>
      </g>
    </svg>
  );
}

function TeamShotChartSection({ participation, currentUserGamePlayerId, periodFilter }) {
  const gamePlayers = participation.gamePlayers || [];

  const [selectedIds, setSelectedIds] = useState(() => {
    const isCurrentUserTeam = currentUserGamePlayerId && gamePlayers.some((p) => p.id === currentUserGamePlayerId);
    return isCurrentUserTeam ? [currentUserGamePlayerId] : gamePlayers.map((p) => p.id);
  });
  const [showMakes, setShowMakes] = useState(true);
  const [showMisses, setShowMisses] = useState(true);
  const [dialogEventId, setDialogEventId] = useState(null);
  const svgRef = useRef(null);
  const windowSize = useWindowSize();
  const mobileLayout = windowSize.width < MOBILE_LAYOUT_BREAKPOINT;

  const allEvents = useMemo(
    () => gamePlayers.flatMap((gp) => (gp.events || []).map((e) => ({ ...e, gamePlayerId: gp.id }))),
    [gamePlayers],
  );

  // Base events: player + period filter — used for FG% (unaffected by shot type toggle)
  const baseEvents = useMemo(() => {
    let filtered = allEvents.filter((e) => selectedIds.includes(e.gamePlayerId));
    if (periodFilter !== "all") filtered = filtered.filter((e) => e.period?.name === periodFilter);
    return filtered;
  }, [allEvents, selectedIds, periodFilter]);

  const events = useMemo(() => {
    if (showMakes && showMisses) return baseEvents;
    if (!showMakes && !showMisses) return [];
    if (showMakes) return baseEvents.filter((e) => e.made === true);
    return baseEvents.filter((e) => e.made === false);
  }, [baseEvents, showMakes, showMisses]);

  const allSelected = selectedIds.length === gamePlayers.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const togglePlayer = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleAll = () => setSelectedIds(allSelected ? [] : gamePlayers.map((p) => p.id));

  const handleDownload = () => {
    if (svgRef.current) {
      const teamName = participation.team?.name || "team";
      downloadSvgAsPng(svgRef.current, `shotchart-${teamName.toLowerCase().replace(/\s+/g, "-")}.png`);
    }
  };

  /* Download button style */
  const pillBtn = (active) => ({
    background: active ? COLOR_BLACK : "transparent",
    color: active ? COLOR_WHITE : LINE,
    borderRadius: 20,
    cursor: "pointer",
    fontSize: 11,
    fontFamily: FONT_FAMILY_GRAPHIK,
    fontWeight: active ? 500 : 400,
    padding: "6px 14px",
    lineHeight: 1.4,
  });

  return (
    <div style={{ flex: "1 1 300px", minWidth: 0, display: "flex", flexDirection: "column", gap: SPACING }}>
      <HighlightPlayerDialog open={!!dialogEventId} eventId={dialogEventId} onClose={() => setDialogEventId(null)} />
      <div
        style={{
          fontFamily: FONT_FAMILY_GRAPHIK,
          fontWeight: 500,
          fontSize: 14,
          letterSpacing: 0.4,
          color: COLOR_BLACK,
          borderBottom: `1px solid ${COLOR_GREY_BORDER}`,
          paddingBottom: SPACING_DENSE,
        }}
      >
        {participation.team?.name}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: mobileLayout ? "column" : "row",
          gap: SPACING,
          alignItems: mobileLayout ? "stretch" : "flex-start",
        }}
      >
        <div style={{ order: mobileLayout ? 2 : 1, width: mobileLayout ? "100%" : 190, flexShrink: 0 }}>
          <div
            onClick={toggleAll}
            style={{
              display: "flex",
              alignItems: "center",
              gap: SPACING_DENSE,
              cursor: "pointer",
              userSelect: "none",
              marginBottom: SPACING_DENSE,
            }}
          >
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              size="small"
              sx={{
                padding: 0,
                color: COLOR_GREY_BORDER,
                "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#F1394B" },
              }}
            />
            <span style={{ fontFamily: FONT_FAMILY_GRAPHIK, fontSize: 13, fontWeight: 500, color: COLOR_BLACK }}>
              All Players
            </span>
          </div>
          <div
            style={
              mobileLayout
                ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: SPACING_DENSE }
                : { display: "flex", flexDirection: "column", gap: SPACING_DENSE, maxHeight: 400, overflowY: "auto" }
            }
          >
            {gamePlayers.map((gp) => {
              const checked = selectedIds.includes(gp.id);
              const label = `${gp.person?.fullNameWithAnonymisation}${gp.playerNumber ? ` - #${gp.playerNumber}` : ""}`;
              return (
                <div
                  key={gp.id}
                  onClick={() => togglePlayer(gp.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: SPACING_DENSE,
                    cursor: "pointer",
                    userSelect: "none",
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <Checkbox
                    checked={checked}
                    size="small"
                    sx={{
                      padding: 0,
                      color: COLOR_GREY_BORDER,
                      "&.Mui-checked": { color: "#F1394B" },
                    }}
                  />
                  <span
                    style={{
                      fontFamily: FONT_FAMILY_GRAPHIK,
                      fontSize: 13,
                      color: COLOR_BLACK,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div
          style={{
            order: mobileLayout ? 1 : 2,
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: SPACING_DENSE,
          }}
        >
          {/* Makes / Misses toggles */}
          <Stack horizontal dense alignItemsCenter>
            <SectionLabel label="Makes" selected={showMakes} onClick={() => setShowMakes((v) => !v)} />
            <SectionLabel label="Misses" selected={showMisses} onClick={() => setShowMisses((v) => !v)} />
          </Stack>

          {/* Court SVG — centred on mobile via block + margin auto */}
          <div style={{ border: `1px solid ${COLOR_GREY_BORDER}`, display: "block" }}>
            <ShotChartSvg events={events} svgRef={svgRef} onClickEvent={setDialogEventId} />
          </div>

          {/* Download below chart and right-aligned */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button style={pillBtn(true)} onClick={handleDownload}>
              Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ShotChart({ gameId, currentUserGamePlayerId }) {
  const [periodFilter, setPeriodFilter] = useState("all");

  const [data] = useData(
    gql`
      query ShotChart($gameId: ID!) {
        game(id: $gameId) {
          id
          participations {
            id
            isHomeTeam
            team {
              id
              name
            }
            gamePlayers {
              id
              playerNumber
              person {
                id
                fullNameWithAnonymisation
              }
              events(hasCoordinates: true) {
                id
                made
                period {
                  name
                }
                coordinates {
                  x
                  y
                }
              }
            }
          }
        }
      }
    `,
    { gameId },
    { skip: !gameId },
  );

  const game = data?.game;
  const participations = game?.participations || [];

  const homeParticipation = participations.find((p) => p.isHomeTeam) || participations[0];
  const awayParticipation = participations.find((p) => !p.isHomeTeam) || participations[1];

  const allEvents = useMemo(
    () => participations.flatMap((p) => (p.gamePlayers || []).flatMap((gp) => gp.events || [])),
    [participations],
  );

  const periods = useMemo(() => {
    const names = new Set(allEvents.map((e) => e.period?.name).filter(Boolean));
    return [...names].sort();
  }, [allEvents]);

  if (!game || allEvents.length === 0) return null;

  return (
    <Section title="SHOT CHART">
      {/* Period filter */}
      <Stack horizontal dense alignItemsCenter style={{ flexWrap: "wrap" }}>
        <SectionLabel label="All Periods" selected={periodFilter === "all"} onClick={() => setPeriodFilter("all")} />
        {periods.map((p) => (
          <SectionLabel key={p} label={p} selected={periodFilter === p} onClick={() => setPeriodFilter(p)} />
        ))}
      </Stack>

      {/* Home + Away sections */}
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: SPACING, marginTop: SPACING_DENSE, alignItems: "flex-start" }}
      >
        {homeParticipation && (
          <TeamShotChartSection
            key={homeParticipation.id}
            participation={homeParticipation}
            currentUserGamePlayerId={currentUserGamePlayerId}
            periodFilter={periodFilter}
          />
        )}
        {awayParticipation && awayParticipation !== homeParticipation && (
          <TeamShotChartSection
            key={awayParticipation.id}
            participation={awayParticipation}
            currentUserGamePlayerId={currentUserGamePlayerId}
            periodFilter={periodFilter}
          />
        )}
      </div>
    </Section>
  );
}
